import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useProjects } from "./ProjectContext";
import AppGallery from "../components/AppGallery";
import Reveal from "../components/Reveal";

const StoreLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="btn-ghost"
  >
    {children}
  </a>
);

const ProjectDetail = () => {
  const { id } = useParams();
  const { projects } = useProjects();
  const navigate = useNavigate();
  const project = projects.find((p) => p.id === id);
  const [showGallery, setShowGallery] = useState(false);

  if (!project)
    return (
      <div className="bg-ink min-h-screen pt-40 text-center text-muted">
        Project not found.
      </div>
    );

  const {
    name,
    tagline,
    category,
    platforms = [],
    gallery = [],
    links = {},
    type = "app",
    client,
    clientConfidential,
    services = [],
    stack = [],
    designCredit,
    designImage,
    award,
  } = project;

  const hasStoreLink = links.appStore || links.googlePlay || links.website;
  const hasGallery = gallery.length > 0;
  const frame = type === "web" ? "browser" : "phone";

  return (
    <div className="bg-ink pt-20">
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden px-6 pt-20 pb-16">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_60%_at_35%_35%,rgba(91,62,255,0.16)_0%,transparent_70%)]" />
        <div className="pointer-events-none absolute inset-0 hero-grid" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em]
                       text-muted hover:text-paper mb-12 transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 19l-7-7 7-7" />
            </svg>
            BACK TO PORTFOLIO
          </button>

          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-brand-cyan
                             border border-ink-line bg-ink-card rounded-full px-3 py-1.5">
              {category}
            </span>
            {platforms.map((p) => (
              <span
                key={p}
                className="font-mono text-[10px] tracking-[0.18em] text-muted
                           border border-ink-line rounded-full px-3 py-1.5"
              >
                {p}
              </span>
            ))}
          </div>

          {(client || clientConfidential) && (
            <p className="font-mono text-[11px] tracking-[0.2em] text-brand-cyan mb-4">
              {client ? `CLIENT PROJECT · ${client.toUpperCase()}` : "CLIENT PROJECT"}
            </p>
          )}

          {award && (
            <p className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full
                          bg-brand-yellow/10 border border-brand-yellow/40
                          text-brand-yellow font-mono text-[10px] tracking-[0.2em]">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="6" />
                <path d="M8.2 13.9 7 22l5-3 5 3-1.2-8.1" />
              </svg>
              {award.toUpperCase()}
            </p>
          )}

          <h1 className="font-display text-[clamp(48px,9vw,110px)] leading-[0.92] tracking-wide text-paper max-w-4xl">
            {name}
          </h1>
          <p className="mt-6 text-muted text-[17px] leading-relaxed max-w-2xl">
            {tagline}
          </p>
        </div>
      </section>

      {/* ===== Main ===== */}
      <div
        className={`max-w-6xl mx-auto px-6 pb-24 flex gap-14 lg:gap-20 ${
          type === "web"
            ? "flex-col items-center"
            : "flex-col lg:flex-row items-start"
        }`}
      >
        {hasGallery && (
          <div
            className={`w-full flex justify-center ${
              type === "web" ? "" : "lg:w-auto lg:sticky lg:top-32"
            }`}
          >
            <AppGallery items={gallery} frame={frame} />
          </div>
        )}

        <div className={type === "web" ? "w-full max-w-3xl" : "flex-1 max-w-xl"}>
          <p className="eyebrow mb-5">Overview</p>
          <h2 className="font-display text-[clamp(32px,5vw,52px)] leading-none tracking-wide text-paper mb-6">
            What is {name.split("—")[0].trim()}?
          </h2>
          <div className="w-20 h-[3px] rounded-full bg-brand-grad mb-8" />

          <p className="text-body text-muted mb-10 whitespace-pre-line">
            {project.description}
          </p>

          {services.length > 0 && (
            <div className="mb-10">
              <h3 className="eyebrow mb-4">What we delivered</h3>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <span
                    key={s}
                    className="px-4 py-2 rounded-full text-xs font-medium
                               bg-brand/10 text-paper border border-brand/30"
                  >
                    {s}
                  </span>
                ))}
              </div>
              {designCredit && (
                <p className="mt-4 text-xs text-muted">
                  {designCredit.replace(" with Understated", "")} with our design
                  partner{" "}
                  <a
                    href="https://understated.me/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand-cyan hover:underline"
                  >
                    Understated
                  </a>
                  .
                </p>
              )}
            </div>
          )}

          {stack.length > 0 && (
            <div className="mb-10">
              <h3 className="eyebrow mb-4">Built with</h3>
              <div className="flex flex-wrap gap-2">
                {stack.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[11px] tracking-wider px-3.5 py-2 rounded-lg
                               border border-ink-line text-muted bg-ink"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {hasGallery && (
              <button onClick={() => setShowGallery(true)} className="btn-primary">
                See gallery
              </button>
            )}
            {links.appStore && (
              <StoreLink href={links.appStore}>App Store</StoreLink>
            )}
            {links.googlePlay && (
              <StoreLink href={links.googlePlay}>Google Play</StoreLink>
            )}
            {links.demo && (
              <Link to={links.demo} className="btn-ghost">
                Try the clinician demo
              </Link>
            )}
            {links.website && (
              <StoreLink href={links.website}>
                {type === "app" ? "Website" : "Visit site"}
              </StoreLink>
            )}
            {!hasStoreLink && type === "app" && (
              <span className="px-4 py-3 text-muted text-sm italic self-center">
                Store link coming soon
              </span>
            )}
          </div>

          <Link
            to="/portfolio"
            className="inline-block mt-10 font-mono text-[11px] tracking-[0.2em] text-muted hover:text-brand-cyan transition-colors"
          >
            ← ALL WORK
          </Link>
        </div>
      </div>

      {/* ===== Design board ===== */}
      {designImage && (
        <section className="border-t border-ink-line bg-ink-soft px-6 py-24">
          <div className="max-w-5xl mx-auto">
            <Reveal className="text-center mb-12">
              <p className="eyebrow mb-5">Design</p>
              <h2 className="display-title">
                {designCredit?.replace(" with Understated", "")} by Understated
              </h2>
              <p className="mt-6 text-body text-muted max-w-lg mx-auto">
                Our design partner{" "}
                <a
                  href="https://understated.me/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand-cyan hover:underline"
                >
                  Understated
                </a>{" "}
                shaped how this product looks and feels.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <img
                src={designImage}
                alt={`${name} design work by Understated`}
                loading="lazy"
                className="w-full max-w-3xl mx-auto rounded-tile border border-ink-line shadow-card"
              />
            </Reveal>
          </div>
        </section>
      )}

      {/* ===== Lightbox ===== */}
      {showGallery && (
        <div
          className="fixed inset-0 z-50 bg-ink/97 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowGallery(false)}
        >
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-ink-card border border-ink-line
                       text-paper text-2xl leading-none flex items-center justify-center
                       hover:border-brand transition-colors"
            aria-label="Close gallery"
          >
            ×
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <AppGallery items={gallery} size="large" frame={frame} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
